// Maps the FinOps visualizer API payload (`data.nodes` / `data.relations`) onto
// the laid-out shape FinOpsVisualizerGraph draws: nodes with x/y + render hints,
// links, and cost badges. Every API field is kept on the node (spread first), so
// id, type, clickable, resource_id, amount, count, etc. stay available later.

// Relations that hang a node off the resource (drawn as a curved branch above
// it) rather than continuing the left-to-right chain.
const BRANCH_RELATION_TYPES = ['HAS_OPPORTUNITY', 'HAS_FINDING'];

// Layout, in the SVG's own coordinate space (same positions the static mock used).
const CHAIN_START_X = 90;
const CHAIN_STEP_X = 195;
const CHAIN_Y = 210;
const BRANCH_Y = 60;
const BRANCH_OFFSETS_X = [-65, 120]; // first branch left of its parent, second right
const BRANCH_EXTRA_STEP_X = 185; // any further branches continue to the right
const COST_BADGE_Y = 150;
const RESOURCE_BADGE_Y = 296;

// Render hints per API node type. `accent` picks a branch node's pastel fill.
// Whether a node reacts to clicks comes from the API's own `clickable` flag.
const NODE_TYPE_HINTS = {
  INTERNET: () => ({ resourceType: 'internet', color: '#2f6df6', sublabel: null }),
  APPLICATION: () => ({ resourceType: 'AWS.ELB.LoadBalancer', color: '#f97316', sublabel: 'Endpoint' }),
  RESOURCE: node => ({ resourceType: node.resource_type, sublabel: node.resource_type }),
  COST_OPTIMIZATION: () => ({ icon: 'lucide-hand-coins', color: '#FFFFFF', sublabel: null, accent: 'cost' }),
  VULNERABILITY: () => ({ icon: 'lucide-binoculars', color: '#FFFFFF', sublabel: null, accent: 'vuln' }),
};

const formatAmount = (amount, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);

export const mapFinOpsGraphData = apiData => {
  const apiNodes = Array.isArray(apiData?.nodes) ? apiData.nodes : [];
  const relations = Array.isArray(apiData?.relations) ? apiData.relations : [];
  if (!apiNodes.length) return null;

  const nodeIdsMap = {};
  apiNodes.forEach(n => {
    const hints = NODE_TYPE_HINTS[n.type]?.(n) ?? { resourceType: n.resource_type || n.type, sublabel: null };
    nodeIdsMap[n.id] = { ...n, ...hints, label: n.label };
  });

  const isBranch = r => BRANCH_RELATION_TYPES.includes(r.type);
  const chainRelations = relations.filter(r => !isBranch(r) && nodeIdsMap[r.source] && nodeIdsMap[r.target]);
  const branchRelations = relations.filter(r => isBranch(r) && nodeIdsMap[r.source] && nodeIdsMap[r.target]);
  const branchNodeIds = new Set(branchRelations.map(r => r.target));

  // Chain order: walk outgoing chain relations from the entry node (Internet, or
  // whichever node nothing points to), like getTreeData's root lookup.
  const outgoing = {};
  chainRelations.forEach(r => {
    (outgoing[r.source] = outgoing[r.source] || []).push(r.target);
  });
  const incoming = new Set(chainRelations.map(r => r.target));
  const chainCandidates = apiNodes.filter(n => !branchNodeIds.has(n.id));
  const root =
    chainCandidates.find(n => n.type === 'INTERNET') ??
    chainCandidates.find(n => !incoming.has(n.id)) ??
    chainCandidates[0];

  const chainOrder = [];
  const visited = new Set();
  const queue = root ? [root.id] : [];
  while (queue.length) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    chainOrder.push(id);
    (outgoing[id] || []).forEach(next => !visited.has(next) && queue.push(next));
  }
  // Chain nodes the walk couldn't reach still get drawn, at the end.
  chainCandidates.forEach(n => !visited.has(n.id) && chainOrder.push(n.id));

  chainOrder.forEach((id, i) => {
    nodeIdsMap[id].x = CHAIN_START_X + i * CHAIN_STEP_X;
    nodeIdsMap[id].y = CHAIN_Y;
  });

  // Branch nodes sit above their parent. The renderer draws a branch link from
  // the branch node down into the parent, so those links are flipped here; the
  // API relation is kept on the link as `relation`.
  const links = chainRelations.map(r => ({ source: r.source, target: r.target, arrow: 'none', relation: r }));
  const badges = [];
  const branchCountByParent = {};
  branchRelations.forEach(r => {
    const parent = nodeIdsMap[r.source];
    const branch = nodeIdsMap[r.target];
    const index = (branchCountByParent[r.source] = (branchCountByParent[r.source] ?? -1) + 1);
    const offsetX =
      BRANCH_OFFSETS_X[index] ??
      BRANCH_OFFSETS_X[BRANCH_OFFSETS_X.length - 1] + (index - BRANCH_OFFSETS_X.length + 1) * BRANCH_EXTRA_STEP_X;
    branch.x = (parent.x ?? CHAIN_START_X) + offsetX;
    branch.y = BRANCH_Y;
    links.push({ source: r.target, target: r.source, arrow: 'none', type: 'branch', relation: r });

    // Savings delta under the cost node, and the matching cost under the resource.
    if (branch.type === 'COST_OPTIMIZATION' && branch.amount != null) {
      const amount = formatAmount(branch.amount, branch.currency);
      badges.push({ text: `-${amount}`, x: branch.x, y: COST_BADGE_Y, variant: 'negative' });
      badges.push({ text: `+${amount}`, x: parent.x, y: RESOURCE_BADGE_Y, variant: 'positive' });
    }
  });

  return { nodes: Object.values(nodeIdsMap), links, badges };
};
